/* ==========================================================================
   BL101 Careers — Rippling job board
   --------------------------------------------------------------------------
   Renders your live Rippling openings as native, fully styleable markup.

   Source: https://api.rippling.com/platform/api/ats/v1/board/<slug>/jobs
   This is the same public feed the official Rippling embed reads. Each row
   links to the real Rippling job page, so the entire application flow —
   forms, resumes, candidate data — still happens inside Rippling. We only
   read the list of open roles.

   If the feed is unreachable for any reason (network, CORS, Rippling
   changing the endpoint), the script silently swaps in the official
   Rippling iframe embed so the page never shows an empty board.

   Every row's destination URL is run through safeUrl() before it reaches an
   href — the feed is third-party data, so its URLs are validated, not
   trusted. Rows that fail validation are dropped; if nothing survives, we
   fall back to the embed rather than render an empty board.
   ========================================================================== */

(function () {
  'use strict';

  var FEED = 'https://api.rippling.com/platform/api/ats/v1/board/{slug}/jobs';
  var EMBED = 'https://ats.rippling.com/embed/{slug}/jobs';

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* esc() makes a string safe to sit INSIDE markup; it is not URL validation.
     `javascript:alert(1)` contains none of the characters esc() rewrites, so
     escaping alone would let a hostile feed value land in an href and run in
     our origin on click. (Escaped quotes don't help either — the browser
     decodes entities while parsing the attribute, reassembling the payload.)
     So resolve the value and hard-require https on Rippling's own hosts.
     Returns '' for anything else; callers must treat that as "drop the row". */
  function safeUrl(u) {
    var a = document.createElement('a');
    a.href = String(u == null ? '' : u);
    if (a.protocol !== 'https:') return '';
    if (a.hostname !== 'rippling.com' && !/\.rippling\.com$/.test(a.hostname)) return '';
    return a.href;
  }

  function BLJobBoard(root) {
    this.root = root;
    this.slug = root.dataset.slug;
    this.mode = root.dataset.mode || 'feed'; // 'feed' | 'embed'
    this.showFilters = root.dataset.filters === 'true';
    this.ctaLabel = root.dataset.ctaLabel || 'Apply';
    this.limit = parseInt(root.dataset.limit, 10) || 0; // 0 = no limit

    this.listEl = root.querySelector('[data-jobs-list]');
    this.barEl = root.querySelector('[data-jobs-filters]');
    this.stateEl = root.querySelector('[data-jobs-state]');
    this.embedEl = root.querySelector('[data-jobs-embed]');

    this.jobs = [];
    this.filter = 'all';

    if (this.mode === 'embed') {
      this.renderEmbed();
      return;
    }
    this.load();
  }

  BLJobBoard.prototype.setState = function (msg) {
    if (!this.stateEl) return;
    this.stateEl.textContent = msg;
    this.stateEl.hidden = !msg;
  };

  BLJobBoard.prototype.load = function () {
    var self = this;
    var url = FEED.replace('{slug}', encodeURIComponent(this.slug));

    fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!Array.isArray(data)) throw new Error('Unexpected payload');

        // Validate each row's link before it can reach the DOM, and keep only
        // the rows we're willing to render.
        var usable = [];
        var dropped = 0;
        data.forEach(function (job) {
          var href = safeUrl(job && job.url);
          if (!href) {
            dropped++;
            return;
          }
          job.safeHref = href;
          usable.push(job);
        });

        if (dropped && window.console) {
          console.warn(
            '[BL101 careers] Dropped ' +
              dropped +
              ' job row(s): url was not an https link on a rippling.com host.'
          );
        }

        // The feed answered, but nothing passed validation. Treat that like a
        // failed fetch and hand off to the official embed rather than showing
        // a board that looks empty when it isn't.
        if (data.length && !usable.length) throw new Error('No rows with a usable URL');

        self.jobs = usable;
        if (!usable.length) {
          self.setState('No openings posted right now — check back soon.');
          return;
        }
        self.setState('');
        if (self.showFilters) self.renderFilters();
        self.renderList();
        self.root.dispatchEvent(
          new CustomEvent('bl:jobs:loaded', {
            bubbles: true,
            detail: { count: usable.length },
          })
        );
      })
      .catch(function (err) {
        if (window.console)
          console.warn('[BL101 careers] Rippling feed unavailable, falling back to embed.', err);
        self.renderEmbed();
      });
  };

  /* -- Filters ------------------------------------------------------------ */

  BLJobBoard.prototype.departments = function () {
    var seen = [];
    this.jobs.forEach(function (job) {
      var label = job.department && job.department.label;
      if (label && seen.indexOf(label) === -1) seen.push(label);
    });
    return seen.sort();
  };

  BLJobBoard.prototype.renderFilters = function () {
    if (!this.barEl) return;
    var depts = this.departments();
    if (depts.length < 2) {
      this.barEl.hidden = true;
      return;
    }

    var self = this;
    var host = this.barEl.querySelector('[data-jobs-chips]') || this.barEl;
    host.innerHTML =
      '<button type="button" class="bl-chip is-active" data-dept="all">All</button>' +
      depts
        .map(function (d) {
          return (
            '<button type="button" class="bl-chip" data-dept="' +
            esc(d) +
            '">' +
            esc(d) +
            '</button>'
          );
        })
        .join('');

    host.addEventListener('click', function (e) {
      var btn = e.target.closest('.bl-chip');
      if (!btn) return;
      self.filter = btn.dataset.dept;
      host.querySelectorAll('.bl-chip').forEach(function (c) {
        c.classList.toggle('is-active', c === btn);
      });
      self.renderList();
    });

    this.barEl.hidden = false;
  };

  /* -- List --------------------------------------------------------------- */

  BLJobBoard.prototype.renderList = function () {
    if (!this.listEl) return;
    var self = this;

    var rows = this.jobs.filter(function (job) {
      if (self.filter === 'all') return true;
      return job.department && job.department.label === self.filter;
    });

    if (this.limit) rows = rows.slice(0, this.limit);

    if (!rows.length) {
      this.listEl.innerHTML = '';
      this.setState('Nothing open in that team right now.');
      return;
    }
    this.setState('');

    this.listEl.innerHTML = rows
      .map(function (job) {
        var dept = (job.department && job.department.label) || '';
        var loc = (job.workLocation && job.workLocation.label) || '';
        return (
          '' +
          '<li>' +
          // safeHref is set in load(); esc() still applies because this is an
          // attribute context, so scheme validation and escaping both matter.
          '<a class="bl-job" href="' +
          esc(job.safeHref) +
          '" target="_blank" rel="noopener noreferrer">' +
          '<h3 class="bl-job__title">' +
          esc(job.name) +
          '</h3>' +
          '<span class="bl-job__meta">' +
          esc(dept) +
          '</span>' +
          '<span class="bl-job__meta">' +
          esc(loc) +
          '</span>' +
          '<span class="bl-job__cta">' +
          esc(self.ctaLabel) +
          ' <span aria-hidden="true">&rarr;</span></span>' +
          '</a>' +
          '</li>'
        );
      })
      .join('');
  };

  /* -- Fallback: the official Rippling iframe ----------------------------- */

  BLJobBoard.prototype.renderEmbed = function () {
    if (!this.embedEl) return;
    if (this.barEl) this.barEl.hidden = true;
    if (this.listEl) this.listEl.innerHTML = '';
    this.setState('');

    var frame = document.createElement('iframe');
    frame.id = 'rr-job-board-iframe';
    frame.src = EMBED.replace('{slug}', encodeURIComponent(this.slug));
    frame.title = 'Open roles at Baseball Lifestyle 101';
    frame.loading = 'lazy';
    frame.referrerPolicy = 'strict-origin-when-cross-origin';
    frame.style.height = '760px';

    this.embedEl.innerHTML = '';
    this.embedEl.appendChild(frame);
    this.embedEl.hidden = false;
    this.root.classList.add('bl-jobs--embedded');
  };

  /* -- Boot --------------------------------------------------------------- */

  function init(scope) {
    (scope || document).querySelectorAll('[data-job-board]').forEach(function (el) {
      if (el.dataset.blInit === '1') return;
      el.dataset.blInit = '1';
      new BLJobBoard(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Re-init when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
